import { create } from 'zustand';
import { Color, Condition, FuelType, Category } from '@/utils/types';
import { supabase } from '@/lib/supabase';

interface GeneralStore {
  // States
  colors: Color[];
  conditions: Condition[];
  fuelTypes: FuelType[];
  categories: Category[];
  isLoading: boolean;
  error: string | null;

  // Actions
  setColors: (colors: Color[]) => void;
  setConditions: (conditions: Condition[]) => void;
  setFuelTypes: (fuelTypes: FuelType[]) => void;
  setCategories: (categories: Category[]) => void;
  setIsLoading: (isLoading: boolean) => void;
  setError: (error: string | null) => void;

  // Initialize function
  initializeStore: () => Promise<void>;
  /**
   * Carga los catálogos ya resueltos por el servidor (lib/catalogos). Sólo
   * si vienen los cuatro con datos: con alguno vacío, `initializeStore` los
   * pide todos como siempre.
   */
  hydrateCatalogs: (c: { colors: unknown[]; conditions: unknown[]; fuelTypes: unknown[]; categories: unknown[] }) => void;
}

export const useGeneralStore = create<GeneralStore>()((set, get) => ({
  // Initial states
  colors: [],
  conditions: [],
  fuelTypes: [],
  categories: [],
  isLoading: true,
  error: null,

  // Actions
  setColors: (colors) => set({ colors }),
  setConditions: (conditions) => set({ conditions }),
  setFuelTypes: (fuelTypes) => set({ fuelTypes }),
  setCategories: (categories) => set({ categories }),
  setIsLoading: (isLoading) => set({ isLoading }),
  setError: (error) => set({ error }),

  hydrateCatalogs: (c) => {
    const s = get();
    if (s.colors.length && s.conditions.length && s.fuelTypes.length && s.categories.length) return;
    if (!c?.colors?.length || !c?.conditions?.length || !c?.fuelTypes?.length || !c?.categories?.length) return;
    set({
      colors: c.colors as Color[],
      conditions: c.conditions as Condition[],
      fuelTypes: c.fuelTypes as FuelType[],
      categories: c.categories as Category[],
      isLoading: false,
      error: null,
    });
  },

  // Initialize function
  initializeStore: async () => {
    const store = get();
    if (
      store.colors.length > 0 &&
      store.conditions.length > 0 &&
      store.fuelTypes.length > 0 &&
      store.categories.length > 0
    ) {
      return; // Data already loaded
    }

    // Dos componentes piden los catálogos antes de que el primero termine
    // (ClientProvider al montar y /vehicles al montar): se comparte la carga
    // en curso en vez de salir a buscar lo mismo dos veces.
    if (catalogosEnCurso) return catalogosEnCurso;

    set({ isLoading: true, error: null });

    catalogosEnCurso = (async () => {
    try {
      // Fetch all data in parallel
      const [
        { data: colors, error: colorsError },
        { data: conditions, error: conditionsError },
        { data: fuelTypes, error: fuelTypesError },
        { data: categories, error: categoriesError },
      ] = await Promise.all([
        supabase.from('colors').select('*').order('id'),
        supabase.from('conditions').select('*').order('id'),
        supabase.from('fuel_types').select('*').order('id'),
        supabase.from('categories').select('*').order('id'),
      ]);

      // Check for errors
      if (colorsError) throw new Error(colorsError.message);
      if (conditionsError) throw new Error(conditionsError.message);
      if (fuelTypesError) throw new Error(fuelTypesError.message);
      if (categoriesError) throw new Error(categoriesError.message);

      // Update store
      set({
        colors: colors || [],
        conditions: conditions || [],
        fuelTypes: fuelTypes || [],
        categories: categories || [],
        isLoading: false,
      });
    } catch (error) {
      set({
        error:
          error instanceof Error
            ? error.message
            : 'An error occurred while fetching data',
        isLoading: false,
      });
    } finally {
      catalogosEnCurso = null;
    }
    })();
    return catalogosEnCurso;
  },
}));

// Carga de catálogos en curso (una sola para todos los que la pidan a la vez).
let catalogosEnCurso: Promise<void> | null = null;
